/**
 * CONTRACTOR PAYABLES — what OVAL owes its works contractors.
 *
 * WHY THIS SCREEN EXISTS AT ALL
 *
 * On 16-09-2026 the CMD went looking for the payable an RA bill had just
 * raised, on Materials > Vendor Payables, and could not find it. The payable
 * was there; that screen's search box simply does not search the payable
 * number, and its tiles are computed from the filtered list, so it read
 * "0 / Rs 0.00 / No payables yet" and looked like a permissions problem.
 *
 * Both are fixed. But the deeper point was the board's: a contractor payable
 * has no goods receipt, no purchase order, no accepted quantity and no
 * rejections. It has a work order, a Measurement Book, retention, TDS and an
 * advance being recovered. Hunting for it on a screen whose own subtitle says
 * "raised when the Head of Stores approves a goods receipt" is looking in the
 * wrong ledger — so it now has this one, and its own CPAY- number.
 *
 * ONE LEDGER, TWO DOORS. These are the same vendor_payables rows the material
 * purchases use. Splitting the table would have meant two ageing reports, two
 * exports and two answers to "what do we owe". The rows stay together; only
 * the doors differ.
 */
import { useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  AlertTriangle, ArrowLeft, Banknote, Calculator, FilterX, Landmark, Receipt,
  Scale, Users, Wallet,
} from 'lucide-react'
import { useAuth } from '@/lib/auth'
import { rcmSupplierClause } from '../lib/reverse-charge'
import { useQuery, useSites } from '@/lib/data'
import { supabase } from '@/lib/supabase'
import { ddmmyyyy, rupees } from '@/lib/format'
import {
  Alert, Card, EmptyState, Loading, SearchInput, Select, Stat, StatusPill, Th,
  sortRows, useSort,
} from '@/components/ui'
import { AuditTrail } from '@/components/docs/shared'
import { RaPayments } from '../components/RaPayments'
import { useServiceSettings } from '../lib/settings'
import { serviceCan } from '../nav'

const n = (v: unknown) => Number(v ?? 0) || 0

interface CpRow {
  id: string; ra_bill_id: string; ra_no: number; wo_id: string; wo_title: string | null
  site_id: string; site_name: string
  vendor_id: string; vendor_name: string; vendor_pan: string | null
  is_gst_registered: boolean
  gross_value: number; gst_amount: number
  retention_amount: number; cum_retention: number
  tds_amount: number; tds_pct: number | null; tds_section: string | null
  advance_recovered: number; advance_outstanding: number
  rcm_gst_amount: number; is_reverse_charge: boolean
  net_payable: number; paid_amount: number; outstanding_amount: number
  payment_count: number; last_paid_on: string | null
  status: string; approval_date: string | null; raised_at: string
  age_days: number; remarks: string | null
}

interface BalanceRow {
  vendor_id: string; vendor_name: string; vendor_pan: string | null
  bills: number; billed: number; paid: number; outstanding: number
  oldest_days: number; over_45_days: number; retention_held: number
}

/** v_contractor_liability, 0140. Per contractor AND per site, which is the pair
 *  a site review is held on. balance_due counts only bills he has invoiced. */
interface LiabRow {
  vendor_id: string; vendor_name: string | null; is_gst_registered: boolean
  site_id: string; site_name: string | null
  bills: number; bills_not_invoiced: number
  invoiceable: number; invoiced: number; paid_cash: number
  settled_by_tds: number; settled_by_advance: number
  retention_held: number; balance_due: number; not_yet_invoiced: number
}

const BLANK = { term: '', status: '', site: '', vendor: '', onlyOwed: false }

export default function ContractorPayables() {
  const { profile } = useAuth()
  const { data: allSites } = useSites()
  const [f, setF] = useState({ ...BLANK })
  const [view, setView] = useState<'bills' | 'contractors'>('bills')
  const { sort, toggle } = useSort('age_days', 'desc')

  const set = (patch: Partial<typeof BLANK>) => setF((v) => ({ ...v, ...patch }))
  const dirty = Object.entries(f).some(([k, v]) => v !== (BLANK as any)[k])

  const { data: rows, loading } = useQuery<CpRow>(
    'v_contractor_payables',
    () => supabase.from('v_contractor_payables').select('*'),
    [], { cacheable: false })

  const { data: balances } = useQuery<BalanceRow>(
    'v_contractor_balances',
    () => supabase.from('v_contractor_balances').select('*'),
    [], { cacheable: false, idField: 'vendor_id' })

  // 0140. What he has INVOICED and what is still owed on it, per contractor and
  // per site — a different question from the CPAY ageing above, which is about
  // bills OVAL has certified whether or not he has billed for them.
  const { data: liability } = useQuery<LiabRow>(
    'v_contractor_liability',
    () => supabase.from('v_contractor_liability').select('*'),
    [], { cacheable: false, idField: 'vendor_id' })

  const shown = useMemo(() => {
    const t = f.term.trim().toLowerCase()
    return sortRows(rows.filter((r) => {
      if (f.status && r.status !== f.status) return false
      if (f.site && r.site_id !== f.site) return false
      if (f.vendor && r.vendor_id !== f.vendor) return false
      if (f.onlyOwed && n(r.outstanding_amount) <= 0.005) return false
      // The payable number IS searchable here. That is the whole reason this
      // screen was built.
      if (t && ![r.id, r.ra_bill_id, r.wo_id, r.vendor_name, r.site_name,
                 r.wo_title, r.vendor_pan]
        .some((v) => String(v ?? '').toLowerCase().includes(t))) return false
      return true
    }), sort, {
      net_payable: 'number', paid_amount: 'number', outstanding_amount: 'number',
      age_days: 'number', raised_at: 'date',
    })
  }, [rows, f, sort])

  const siteOptions = useMemo(
    () => [...new Set(rows.map((r) => r.site_id))]
      .map((id) => ({
        value: id,
        label: (allSites as any[]).find((s) => s.id === id)?.site_name ?? id,
      })).sort((a, b) => a.label.localeCompare(b.label)),
    [rows, allSites])

  const vendorOptions = useMemo(
    () => [...new Map(rows.map((r) => [r.vendor_id, r.vendor_name])).entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    [rows])

  // 0212: when a contractor payable counts as overdue is a Service setting
  // (45 days until changed).
  const { contractorOverdueDays: od } = useServiceSettings()

  if (loading) return <Loading label="Reading the contractor ledger…" />

  const owed = rows.reduce((a, r) => a + n(r.outstanding_amount), 0)
  const over45 = rows.filter((r) => n(r.age_days) > od && n(r.outstanding_amount) > 0.005)
  const over45Value = over45.reduce((a, r) => a + n(r.outstanding_amount), 0)
  const retention = (balances as BalanceRow[]).reduce((a, b) => a + n(b.retention_held), 0)
  const rcmOwed = rows.reduce((a, r) => a + n(r.rcm_gst_amount), 0)

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold text-slate-900">Contractor Payables</h1>
        <p className="text-sm text-slate-500">
          Raised automatically when a running account bill clears its chain — at the net
          figure, the amount that actually leaves the bank.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Outstanding" value={rupees(owed)}
              tone={owed > 0 ? 'warn' : 'good'}
              sub={`across ${rows.length} bill${rows.length === 1 ? '' : 's'}`}
              icon={<Wallet className="h-4 w-4" />} />
        <Stat label={`Over ${od} days`} value={over45.length}
              tone={over45.length ? 'bad' : 'good'}
              sub={rupees(over45Value)}
              icon={<AlertTriangle className="h-4 w-4" />} />
        <Stat label="Retention held" value={rupees(retention)}
              sub="released after the defect liability period"
              icon={<Scale className="h-4 w-4" />} />
        <Stat label="Reverse-charge GST" value={rupees(rcmOwed)}
              sub="OVAL owes this to the government, not to them"
              icon={<Landmark className="h-4 w-4" />} />
      </div>

      {/* WHAT EACH CONTRACTOR IS OWED, AT EACH SITE — added 0140.
          The tiles above answer "what do we owe" for the company. This answers
          "what do we owe HIM, at THAT site", which is the question asked at a
          site review and the one nothing could answer before his invoices had
          anywhere to be recorded. */}
      {liability.length > 0 && (
        <Card title="What each contractor is owed, by site"
              subtitle="Invoiced and not yet settled. Retention held is owed but not due; work certified that he has not invoiced for is exposure, not debt.">
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Contractor</th><th>Site</th>
                  <th className="num">Invoiced</th><th className="num">Paid</th>
                  <th className="num">Still owed</th>
                  <th className="num">Retention held</th>
                  <th className="num">Not yet invoiced</th>
                </tr>
              </thead>
              <tbody>
                {(liability as LiabRow[])
                  .slice()
                  .sort((a, b) => n(b.balance_due) - n(a.balance_due))
                  .map((r) => (
                    <tr key={`${r.vendor_id}|${r.site_id}`}>
                      <td className="max-w-[200px] truncate font-medium">
                        {r.vendor_name}
                        {!r.is_gst_registered && (
                          <span className="pill-grey ml-1" title="Unregistered — reverse charge">RCM</span>
                        )}
                      </td>
                      <td className="max-w-[180px] truncate text-xs">{r.site_name}</td>
                      <td className="num">{rupees(r.invoiced)}</td>
                      <td className="num text-emerald-700">{rupees(r.paid_cash)}</td>
                      <td className={n(r.balance_due) > 0.005
                        ? 'num font-semibold text-amber-700' : 'num'}>
                        {rupees(r.balance_due)}
                      </td>
                      <td className="num text-xs text-slate-500">{rupees(r.retention_held)}</td>
                      <td className="num text-xs">
                        {n(r.not_yet_invoiced) > 0.005
                          ? <span className="text-amber-700">{rupees(r.not_yet_invoiced)}</span>
                          : <span className="text-slate-300">—</span>}
                        {n(r.bills_not_invoiced) > 0 && (
                          <span className="block text-[11px] text-slate-400">
                            {r.bills_not_invoiced} bill{n(r.bills_not_invoiced) === 1 ? '' : 's'} unbilled
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600">
        <Receipt className="mr-1 inline h-3.5 w-3.5" />
        These are numbered <strong>CPAY-</strong> and are the same rows the material
        purchases use — one creditor ledger, a second door onto it. They no longer appear on{' '}
        <strong>Material Procurement → Vendor Payables</strong>, which is for goods receipts.
        Recording a payment is done on the bill itself, where the bank advice is attached.
      </div>

      <div className="flex flex-wrap gap-1 border-b border-slate-200">
        {([['bills', 'Bill by bill'], ['contractors', 'By contractor']] as const).map(([k, l]) => (
          <button key={k} onClick={() => setView(k)}
                  className={view === k
                    ? 'flex items-center gap-1.5 border-b-2 border-amber-500 px-3 py-2 text-sm font-semibold text-amber-900'
                    : 'flex items-center gap-1.5 border-b-2 border-transparent px-3 py-2 text-sm text-slate-500 hover:text-slate-800'}>
            {k === 'bills' ? <Receipt className="h-4 w-4" /> : <Users className="h-4 w-4" />} {l}
          </button>
        ))}
      </div>

      {view === 'contractors' ? (
        <Card title="What each contractor is owed"
              subtitle="Every approved bill, summed. The question a director actually asks.">
          {(balances as BalanceRow[]).length === 0 ? (
            <EmptyState icon={<Users className="h-8 w-8" />} title="Nothing owed to anybody" />
          ) : (
            <div className="overflow-x-auto">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Contractor</th><th>PAN</th>
                    <th className="num">Bills</th>
                    <th className="num">Billed</th>
                    <th className="num">Paid</th>
                    <th className="num">Outstanding</th>
                    <th className="num">Oldest</th>
                    <th className="num">Retention held</th>
                  </tr>
                </thead>
                <tbody>
                  {(balances as BalanceRow[])
                    .sort((a, b) => n(b.outstanding) - n(a.outstanding))
                    .map((b) => (
                    <tr key={b.vendor_id}>
                      <td className="max-w-[220px] truncate font-medium">{b.vendor_name}</td>
                      <td className="whitespace-nowrap font-mono text-xs">{b.vendor_pan ?? '—'}</td>
                      <td className="num">{b.bills}</td>
                      <td className="num whitespace-nowrap">{rupees(b.billed)}</td>
                      <td className="num whitespace-nowrap text-emerald-700">{rupees(b.paid)}</td>
                      <td className={n(b.outstanding) > 0
                        ? 'num whitespace-nowrap font-bold text-amber-800'
                        : 'num whitespace-nowrap'}>{rupees(b.outstanding)}</td>
                      <td className={n(b.oldest_days) > od
                        ? 'num font-semibold text-red-700' : 'num'}>
                        {n(b.oldest_days)}d
                      </td>
                      <td className="num whitespace-nowrap">{rupees(b.retention_held)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      ) : (
        <Card title={null}>
          <div className="mb-3 flex flex-wrap gap-2">
            <SearchInput value={f.term} onChange={(v) => set({ term: v })}
                         placeholder="CPAY number, bill, work order, contractor, site or PAN…"
                         className="min-w-[240px] flex-1" />
            <Select value={f.vendor} onChange={(e) => set({ vendor: e.target.value })}
                    className="w-52" placeholder="Every contractor" options={vendorOptions} />
            <Select value={f.site} onChange={(e) => set({ site: e.target.value })}
                    className="w-48" placeholder="Every site" options={siteOptions} />
            <Select value={f.status} onChange={(e) => set({ status: e.target.value })}
                    className="w-44" placeholder="Any status"
                    options={[...new Set(rows.map((r) => r.status))].sort()
                      .map((s) => ({ value: s, label: s }))} />
            <label className="flex cursor-pointer items-center gap-1.5 rounded-lg border border-slate-200 px-3 text-sm text-slate-700">
              <input type="checkbox" className="h-4 w-4 rounded border-slate-300"
                     checked={f.onlyOwed}
                     onChange={(e) => set({ onlyOwed: e.target.checked })} />
              Still owed only
            </label>
            {dirty && (
              <button className="btn-ghost text-slate-600" onClick={() => setF({ ...BLANK })}>
                <FilterX className="h-4 w-4" /> Clear
              </button>
            )}
          </div>

          {shown.length === 0 ? (
            <EmptyState icon={<Wallet className="h-8 w-8" />}
                        title={dirty ? 'Nothing matches those filters' : 'No contractor payables yet'}
                        message={dirty
                          ? `${rows.length} payable(s) are hidden by the filters you have set.`
                          : 'One is raised the moment a running account bill clears the CMD.'} />
          ) : (
            <div className="overflow-x-auto">
              <table className="tbl">
                <thead>
                  <tr>
                    <Th id="id" sort={sort} onSort={toggle}>Payable</Th>
                    <th>Against</th>
                    <th>Contractor</th>
                    <th className="num">Gross</th>
                    <th className="num">Deductions</th>
                    <Th id="net_payable" sort={sort} onSort={toggle} align="right">Net payable</Th>
                    <Th id="paid_amount" sort={sort} onSort={toggle} align="right">Paid</Th>
                    <Th id="outstanding_amount" sort={sort} onSort={toggle} align="right">Outstanding</Th>
                    <Th id="age_days" sort={sort} onSort={toggle} align="right">Age</Th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((r) => {
                    const ded = n(r.retention_amount) + n(r.tds_amount) + n(r.advance_recovered)
                    return (
                      <tr key={r.id}>
                        <td className="whitespace-nowrap font-medium">
                          {/* The number is the way in. A payable the reader can
                              see but not open forces them to guess which RA
                              bill it came from and go round the long way. */}
                          <Link to={`/service/contractor-payables/${r.id}`}
                                className="text-amber-700 hover:underline">
                            {r.id}
                          </Link>
                          <span className="block text-[11px] text-slate-400">
                            {ddmmyyyy(r.raised_at)}
                          </span>
                        </td>
                        <td className="whitespace-nowrap">
                          <Link to={`/service/ra-bills/${r.ra_bill_id}`}
                                className="text-amber-700 hover:underline">
                            RA-{r.ra_no}
                          </Link>
                          <span className="block max-w-[170px] truncate text-[11px] text-slate-400">
                            {r.wo_id} · {r.site_name}
                          </span>
                        </td>
                        <td className="max-w-[170px] truncate">
                          {r.vendor_name}
                          {r.is_reverse_charge && <span className="pill-grey ml-1">RCM</span>}
                        </td>
                        <td className="num whitespace-nowrap">{rupees(r.gross_value)}</td>
                        <td className="num whitespace-nowrap text-xs text-slate-500"
                            title={`Retention ${rupees(r.retention_amount)} · TDS ${rupees(r.tds_amount)} at ${n(r.tds_pct)}% · advance ${rupees(r.advance_recovered)}`}>
                          {rupees(ded)}
                        </td>
                        <td className="num whitespace-nowrap font-semibold">{rupees(r.net_payable)}</td>
                        <td className="num whitespace-nowrap text-emerald-700">
                          {rupees(r.paid_amount)}
                          {r.payment_count > 0 && (
                            <span className="block text-[11px] text-slate-400">
                              {r.payment_count} payment{r.payment_count === 1 ? '' : 's'}
                            </span>
                          )}
                        </td>
                        <td className={n(r.outstanding_amount) > 0
                          ? 'num whitespace-nowrap font-bold text-amber-800'
                          : 'num whitespace-nowrap'}>
                          {rupees(r.outstanding_amount)}
                        </td>
                        <td className={n(r.age_days) > od && n(r.outstanding_amount) > 0.005
                          ? 'num font-semibold text-red-700' : 'num'}>
                          {n(r.age_days)}d
                        </td>
                        <td><StatusPill status={r.status} /></td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {over45.length > 0 && (
        <Alert tone="warning" title={
          `${over45.length} contractor payable${over45.length === 1 ? '' : 's'} older than ${od} days`}>
          {rupees(over45Value)} has been owed for more than {od === 45 ? 'forty-five' : od} days. A works
          contractor who is not paid stops sending men, and the delay costs more than the
          bill. Recording a payment is done on the bill itself.
        </Alert>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// ONE PAYABLE, IN FULL
//
// Asked for on 16-09-2026: "CPAY-20260916-001 should be clickable and open the
// details of this page". The register can only afford one hover-title for the
// whole deduction column, which is exactly the figure somebody queries.
//
// THE SHAPE OF THIS SCREEN IS THE QUESTION IT ANSWERS: a contractor telephones
// and says he was promised forty thousand and forty thousand did not arrive.
// So the middle of the page is a ladder from the gross value to the net, one
// line per deduction, each naming its own authority — the retention percentage
// agreed on the work order, the TDS section and rate read off his PAN, the
// advance being recovered. Nothing here is typed and nothing here is new; it is
// the bill's own arithmetic, shown where the money question gets asked.
//
// THE LADDER IS PROVED, NOT ASSERTED. The last line is computed as the
// residual, so if the deductions this view carries do not explain the whole
// difference between gross and net, the screen says so in words rather than
// printing a total that quietly disagrees with itself.
// ---------------------------------------------------------------------------
export function ContractorPayableDetail() {
  const { id = '' } = useParams()
  const { profile } = useAuth()

  const { data: rows, loading, refresh } = useQuery<CpRow>(
    `v_contractor_payables_${id}`,
    () => supabase.from('v_contractor_payables').select('*').eq('id', id),
    [id], { cacheable: false })
  // 0212: the overdue line is a Service setting (45 days until changed).
  const { contractorOverdueDays: od } = useServiceSettings()

  if (loading) return <Loading label="Reading the payable…" />

  const r = (rows as CpRow[])[0]
  if (!r) {
    return (
      <div className="space-y-4">
        <Link to="/service/contractor-payables"
              className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
          <ArrowLeft className="h-4 w-4" /> Contractor Payables
        </Link>
        <EmptyState icon={<Wallet className="h-8 w-8" />}
                    title={`${id} is not on this ledger`}
                    message="Either the number is wrong, or this payable came from a goods receipt rather than a running account bill — those are on Material Procurement → Vendor Payables." />
      </div>
    )
  }

  const paid = n(r.paid_amount)
  const due  = n(r.outstanding_amount)
  const late = n(r.age_days) > od && due > 0.005
  const canPay = serviceCan(profile?.role, 'recordPayment')

  // Gross + GST, less every deduction, equals net. Anything left over is named.
  const explained = n(r.gross_value) + n(r.gst_amount)
    - n(r.retention_amount) - n(r.tds_amount) - n(r.advance_recovered)
  const residual = n(r.net_payable) - explained

  return (
    <div className="space-y-4">
      <Link to="/service/contractor-payables"
            className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
        <ArrowLeft className="h-4 w-4" /> Contractor Payables
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-bold text-slate-900">{r.id}</h1>
            <StatusPill status={r.status} />
            {r.is_reverse_charge && <span className="pill-grey">Reverse charge</span>}
          </div>
          <p className="text-sm text-slate-500">
            Owed to <strong>{r.vendor_name}</strong> · raised {ddmmyyyy(r.raised_at)} on approval of{' '}
            <Link to={`/service/ra-bills/${r.ra_bill_id}`} className="text-amber-700 hover:underline">
              RA-{r.ra_no}
            </Link>
          </p>
        </div>
      </div>

      {late && (
        <Alert tone="warning" title={`Owed for ${n(r.age_days)} days`}>
          {rupees(due)} has been outstanding since {ddmmyyyy(r.raised_at)}. A works
          contractor who is not paid stops sending men, and the delay costs more than
          the bill.
        </Alert>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Net payable" value={rupees(r.net_payable)}
              sub="what leaves the bank" icon={<Wallet className="h-4 w-4" />} />
        <Stat label="Paid to date" value={rupees(paid)}
              tone={paid > 0 ? 'good' : 'default'}
              sub={r.payment_count > 0
                ? `${r.payment_count} payment${r.payment_count === 1 ? '' : 's'}, last ${ddmmyyyy(r.last_paid_on)}`
                : 'nothing yet'}
              icon={<Banknote className="h-4 w-4" />} />
        <Stat label="Outstanding" value={rupees(due)}
              tone={due > 0.005 ? 'warn' : 'good'}
              sub={due > 0.005 ? undefined : 'settled in full'}
              icon={<Receipt className="h-4 w-4" />} />
        <Stat label="Age" value={`${n(r.age_days)} days`}
              tone={late ? 'bad' : 'default'}
              sub={late ? `past ${od} days` : `within ${od} days`}
              icon={<AlertTriangle className="h-4 w-4" />} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* ---------------- THE LADDER ---------------- */}
        <Card title="How this figure was arrived at"
              subtitle="The bill's own arithmetic. Nobody typed any of it.">
          <table className="tbl">
            <tbody>
              <tr>
                <td>Value of work certified on this bill</td>
                <td className="num whitespace-nowrap font-semibold">{rupees(r.gross_value)}</td>
              </tr>
              {n(r.gst_amount) !== 0 && (
                <tr>
                  <td>GST charged by the contractor</td>
                  <td className="num whitespace-nowrap">{rupees(r.gst_amount)}</td>
                </tr>
              )}
              <tr>
                <td>
                  Less retention
                  <span className="block text-[11px] text-slate-400">
                    held back until the defect liability period ends
                  </span>
                </td>
                <td className="num whitespace-nowrap text-red-700">
                  {n(r.retention_amount) ? `− ${rupees(r.retention_amount)}` : rupees(0)}
                </td>
              </tr>
              <tr>
                <td>
                  Less TDS
                  <span className="block text-[11px] text-slate-400">
                    {r.tds_section ? `${r.tds_section} at ${n(r.tds_pct)}%` : 'no deduction'}
                    {' · '}on the value of work, never on the GST
                  </span>
                </td>
                <td className="num whitespace-nowrap text-red-700">
                  {n(r.tds_amount) ? `− ${rupees(r.tds_amount)}` : rupees(0)}
                </td>
              </tr>
              <tr>
                <td>
                  Less advance recovered
                  <span className="block text-[11px] text-slate-400">
                    {rupees(r.advance_outstanding)} still to recover after this bill
                  </span>
                </td>
                <td className="num whitespace-nowrap text-red-700">
                  {n(r.advance_recovered) ? `− ${rupees(r.advance_recovered)}` : rupees(0)}
                </td>
              </tr>
              {Math.abs(residual) > 0.005 && (
                <tr>
                  <td>
                    Other adjustments recorded on the bill
                    <span className="block text-[11px] text-slate-400">
                      the remainder of the difference — open RA-{r.ra_no} to see it named
                    </span>
                  </td>
                  <td className="num whitespace-nowrap text-red-700">
                    {residual < 0 ? `− ${rupees(Math.abs(residual))}` : rupees(residual)}
                  </td>
                </tr>
              )}
              <tr className="border-t-2 border-slate-300">
                <td className="font-bold">Net payable</td>
                <td className="num whitespace-nowrap text-base font-bold">
                  {rupees(r.net_payable)}
                </td>
              </tr>
            </tbody>
          </table>

          <p className="mt-3 flex gap-1.5 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
            <Calculator className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              A running account bill is cumulative: it states everything certified to date
              and subtracts every earlier approved bill. Retention held on this work order
              so far is <strong>{rupees(r.cum_retention)}</strong>.
            </span>
          </p>

          {r.is_reverse_charge && (
            <p className="mt-2 flex gap-1.5 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
              <Landmark className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                <strong>Reverse charge.</strong> No GST is added above and none is
                deducted from him — it is not part of what he is owed. OVAL pays{' '}
                <strong>{rupees(r.rcm_gst_amount)}</strong> to the Government directly.{' '}
                {rcmSupplierClause({ registered: r.is_gst_registered })}
              </span>
            </p>
          )}
        </Card>

        {/* ---------------- WHERE IT CAME FROM ---------------- */}
        <Card title="Who and what this is against">
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2.5 text-sm">
            <dt className="text-slate-500">Contractor</dt>
            <dd className="font-medium">{r.vendor_name}</dd>

            <dt className="text-slate-500">PAN</dt>
            <dd className="font-mono text-xs">{r.vendor_pan ?? '—'}</dd>

            <dt className="text-slate-500">GST</dt>
            <dd>{r.is_gst_registered ? 'Registered' : 'Not registered — reverse charge applies'}</dd>

            <dt className="text-slate-500">Bill</dt>
            <dd>
              <Link to={`/service/ra-bills/${r.ra_bill_id}`}
                    className="text-amber-700 hover:underline">
                {r.ra_bill_id}
              </Link>
              <span className="text-slate-400"> · RA-{r.ra_no}</span>
            </dd>

            <dt className="text-slate-500">Work order</dt>
            <dd>
              <Link to={`/service/work-orders/${r.wo_id}`}
                    className="text-amber-700 hover:underline">
                {r.wo_id}
              </Link>
              {r.wo_title && (
                <span className="block text-xs text-slate-500">{r.wo_title}</span>
              )}
            </dd>

            <dt className="text-slate-500">Site</dt>
            <dd>{r.site_name}</dd>

            <dt className="text-slate-500">Approved</dt>
            <dd>{r.approval_date ? ddmmyyyy(r.approval_date) : '—'}</dd>

            {r.remarks && (
              <>
                <dt className="text-slate-500">Remarks</dt>
                <dd className="text-slate-600">{r.remarks}</dd>
              </>
            )}
          </dl>

          <p className="mt-4 flex gap-1.5 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
            <Scale className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              This is a row in the same creditor ledger the material purchases use — one
              ledger, two doors, so there is only ever one answer to "what do we owe".
              It was raised automatically when RA-{r.ra_no} cleared the CMD; no one keys a
              contractor payable by hand.
            </span>
          </p>
        </Card>
      </div>

      {/* Payments hang off the BILL, not off the payable — one RPC, one truth.
          This is the same card the RA bill screen shows, imported, not copied. */}
      <RaPayments raBillId={r.ra_bill_id} label={r.id}
                  netPayable={n(r.net_payable)} paidToDate={paid}
                  canPay={canPay} onChanged={refresh} />

      {/* The payable has no chain of its own; it is a consequence of one. The
          trail that matters is the bill's, so that is the one shown. */}
      <AuditTrail docType="RA" docId={r.ra_bill_id} />
    </div>
  )
}
